import { computed, unref } from 'vue';

const PINCODE_REGEX = /\b\d{6}\b/;

export function useAddressText(locRef: any) {
  
  const location = computed(() => unref(locRef));

  const isValidAddress = (address: any) => {
    if (!address?.formatted_address?.trim()) return false;
    return /[a-zA-Z0-9\s.,#-]+/.test(address.formatted_address);
  };

  const appendPincodeIfMissing = (text = '', pincode?: string) => {
    if (!pincode) return text;
    return PINCODE_REGEX.test(text) ? text.trim() : `${text} ${pincode}`.trim();
  };

  const buildFallbackAddress = (loc: any) =>
    [
      loc.address_line1,
      loc.address_line2,
      loc.address_line3,
      loc.city?.name,
      loc.state?.name,
      loc.area_code &&
        !`${loc.address_line1} ${loc.address_line2} ${loc.address_line3} ${loc.city?.name} ${loc.state?.name}`.includes(loc.area_code)
        ? loc.area_code
        : null,
    ]
      .filter(Boolean)
      .join(' ');

  const getFormattedAddress = (loc: any) => {
    if (!loc?.formatted_address) return '';

    const hasLine3 =
      typeof loc.address_line3 === 'string' &&
      loc.address_line3.trim().length > 0;

    if (hasLine3) {
      return `${loc.address_line3} ${loc.area_code || ''}`.trim();
    }

    if (loc.address_line2) {
      return `${loc.address_line2} ${loc.area_code || ''}`.trim();
    }

    return appendPincodeIfMissing(loc.formatted_address, loc.area_code);
  };

  const resolveAddress = (loc: any, mode: 'short' | 'full') => {
    
    if (!loc) return 'Choose your location';

    if (isValidAddress(loc)) {
      return mode === 'full'
        ? appendPincodeIfMissing(loc.formatted_address, loc.area_code)
        : getFormattedAddress(loc);
    }

    return buildFallbackAddress(loc);
  };

  const smartTruncateAddress = (
    text = '',
    maxLength = 42,
    ellipsis = '...'
  ) => {
    if (text.length <= maxLength) return text;

    const suffixMatch = text.match(/,\s*\d{6}$/);
    const suffix = suffixMatch ? suffixMatch[0] : '';
    const base = suffixMatch ? text.replace(suffixMatch[0], '').trim() : text;

    const allowed = maxLength - suffix.length - ellipsis.length;
    if (allowed <= 0) return ellipsis + suffix;

    const words = base.split(' ');
    let result = '';

    for (const word of words) {
      if ((result + ' ' + word).trim().length > allowed) break;
      result = (result + ' ' + word).trim();
    }

    return result + ellipsis + suffix;
  };


  const addressText = computed(() => {
    const address = resolveAddress(location.value, 'short');
    return smartTruncateAddress(address, 60);
  });

  const fullAddressText = computed(() =>
    resolveAddress(location.value, 'full')
  );

  return {
    addressText,
    fullAddressText,
    isValidAddress,
  };
}
